import { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useScan } from "../context/InventoryContext";
import { hasAnyPermission } from "../lib/permissions";

const NAV_SECTIONS = [
  {
    title: "Main",
    links: [
      { label: "Dashboard", path: "/" },
      {
        label: "Retail Management",
        path: "/retail_dashboard",
        requiredPermissions: ["main_admin.view", "main_admin.edit"],
      },
      {
        label: "QC Process",
        path: "/warehouse_dashboard",
        requiredPermissions: ["quality_check.view", "quality_check.edit"],
      },
      // PermissionRoute
      {
        label: "Inventory",
        path: "/inventory-management-dashboard",
        requiredPermissions: ["inventory.view", "inventory.edit"],
      },
      // PermissionRoute
      {
        label: "SKU Management",
        path: "/sku-management",
        requiredPermissions: [
          "sku_management.view",
          "sku_master.view",
          "sku_creation.view",
          "category_master.view",
          "npd_master.view",
          "unit_economics.view",
          "listing_master.view",
          "catalouge_master.view",
          "isku_explorer.view",
          "dropdown_admin.view",
        ],
      },
    ],
  },
  {
    title: "SKU Pages",
    links: [
      {
        label: "SKU Creation",
        path: "/sku-management/sku-creation",
        requiredPermissions: ["sku_creation.view", "sku_creation.create"],
      },
      {
        label: "SKU Master",
        path: "/sku-management/sku-master",
        requiredPermissions: ["sku_master.view", "sku_master.edit"],
      },
      {
        label: "Category Master",
        path: "/sku-management/category",
        requiredPermissions: ["category_master.view", "category_master.edit"],
      },
      {
        label: "Dropdown Admin",
        path: "/sku-management/dropdown-admin",
        requiredPermissions: ["dropdown_admin.view", "dropdown_admin.edit"],
      },
      {
        label: "NPD Master",
        path: "/sku-management/npd",
        requiredPermissions: ["npd_master.view", "npd_master.edit"],
      },
      {
        label: "Unit Economics",
        path: "/sku-management/unit-economics",
        requiredPermissions: ["unit_economics.view", "unit_economics.edit"],
      },
      {
        label: "Listing Master",
        path: "/sku-management/listing-master",
        requiredPermissions: ["listing_master.view", "listing_master.edit"],
      },
      {
        label: "Catalogue Master",
        path: "/sku-management/catalouge-master",
        requiredPermissions: ["catalouge_master.view", "catalouge_master.edit"],
      },
      {
        label: "I-SKU Explorer",
        path: "/sku-management/isku-explorer",
        requiredPermissions: ["isku_explorer.view", "isku_explorer.edit"],
      },
    ],
  },
  {
    title: "Operations",
    links: [
      {
        label: "Batches Data",
        path: "/batches_data",
        requiredPermissions: ["quality_check.view", "quality_check.edit"],
      },
      {
        label: "Inventory Count",
        path: "/inventory-count",
        requiredPermissions: ["main_admin.view", "main_admin.edit"],
      },
      {
        label: "Scanned Result",
        path: "/scanned-result",
        requiredPermissions: ["main_admin.view", "main_admin.edit"],
      },
      {
        label: "Task Section",
        path: "/task-section",
        requiredPermissions: ["inventory.view", "inventory.edit"],
      },
      {
        label: "Batch Viewer",
        path: "/batch-viewer",
        requiredPermissions: ["inventory.view", "inventory.edit"],
      },
      {
        label: "Sales Upload",
        path: "/sales-upload",
        requiredPermissions: ["inventory.edit"],
      },
    ],
  },
  {
    title: "Purchase",
    links: [
      {
        label: "Party Details",
        path: "/purchase/party-details",
        requiredPermissions: ["purchase_admin.view", "purchase_admin.edit"],
      },
      {
        label: "Requirements",
        path: "/purchase/requirements",
        requiredPermissions: ["purchase_admin.view", "purchase_admin.edit"],
      },
      {
        label: "Invoicing Details",
        path: "/purchase/invoicing",
        requiredPermissions: [
          "purchase_admin.view",
          "purchase_admin.edit",
          "purchase_vendor.view",
          "purchase_vendor.edit",
          "purchase_user.view",
          "purchase_user.edit",
        ],
      },
    ],
  },
  {
    title: "Admin",
    links: [
      {
        label: "Images & Remarks",
        path: "/secure/quality-check",
        requiredPermissions: [
          "qc_admin.view",
          "qc_admin.edit",
          "quality_check.view",
          "quality_check.edit",
        ],
      },
      {
        label: "Remarks Summary",
        path: "/secure/remarks-summary",
        requiredPermissions: [
          "qc_admin.view",
          "qc_admin.edit",
          "quality_check.view",
          "quality_check.edit",
        ],
      },
      // PermissionRoute uses main_admin.view / main_admin.edit in App.js
      {
        label: "Warehouse Admin",
        path: "/warehouse-admin-page",
        requiredPermissions: ["main_admin.view", "main_admin.edit"],
      },
      {
        label: "User Permissions",
        path: "/admin/user-permissions",
        requiredPermissions: ["main_admin.view", "main_admin.edit"],
      },
    ],
  },
];

// A link is visible when it has no permission requirement OR the user has at least one
function canSeeLink(link) {
  if (!link.requiredPermissions || link.requiredPermissions.length === 0) return true;
  return hasAnyPermission(link.requiredPermissions);
}

function NavDropdown({ section, currentPath, onNavigate }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const visibleLinks = section.links.filter(canSeeLink);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (visibleLinks.length === 0) return null;

  const hasActive = visibleLinks.some((l) => l.path === currentPath);

  return (
    <div style={{ position: "relative" }} ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          padding: "6px 12px",
          border: `1px solid ${hasActive || open ? "#818cf8" : "#e2e8f0"}`,
          borderRadius: 8,
          background: hasActive || open ? "#eef2ff" : "#fff",
          color: hasActive || open ? "#4f46e5" : "#374151",
          fontSize: 13,
          fontWeight: 500,
          cursor: "pointer",
          whiteSpace: "nowrap",
          transition: "all 0.15s",
        }}
      >
        {section.title}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          style={{
            width: 14,
            height: 14,
            transition: "transform 0.2s",
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
          }}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 8px)",
            left: 0,
            minWidth: 200,
            background: "#fff",
            border: "1px solid #e2e8f0",
            borderRadius: 12,
            boxShadow: "0 8px 24px rgba(15,23,42,0.12)",
            zIndex: 9999,
            overflow: "hidden",
            padding: "4px 0",
          }}
        >
          {visibleLinks.map((link) => {
            const isActive = currentPath === link.path;
            return (
              <button
                key={link.path}
                type="button"
                onClick={() => {
                  onNavigate(link.path);
                  setOpen(false);
                }}
                style={{
                  display: "block",
                  width: "100%",
                  textAlign: "left",
                  padding: "9px 16px",
                  fontSize: 13,
                  fontWeight: isActive ? 600 : 400,
                  color: isActive ? "#4f46e5" : "#374151",
                  background: isActive ? "#eef2ff" : "transparent",
                  border: "none",
                  cursor: "pointer",
                  transition: "background 0.12s",
                }}
                onMouseEnter={(e) => {
                  if (!isActive) e.currentTarget.style.background = "#f8fafc";
                }}
                onMouseLeave={(e) => {
                  if (!isActive) e.currentTarget.style.background = "transparent";
                }}
              >
                {link.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { clearItems } = useScan();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("username");
    localStorage.removeItem("store");
    localStorage.removeItem("access");
    localStorage.removeItem("permissions");
    clearItems();
    navigate("/login");
  };

  const handleNavigate = (path) => {
    setMobileMenuOpen(false);
    if (location.pathname === path) return;
    if (location.pathname === "/scanned-result") {
      if (window.confirm("Are you sure you want to leave? Unsaved progress may be lost.")) {
        navigate(path);
      }
    } else {
      navigate(path);
    }
  };

  const isLoggedIn = !!localStorage.getItem("token");
  const userName = localStorage.getItem("username");
  const visibleSections = NAV_SECTIONS
    .map((section) => ({
      ...section,
      links: section.links.filter(canSeeLink),
    }))
    .filter((section) => section.links.length > 0);

  return (
    <>
      {/* NAV */}
      <nav
        style={{
          position: "sticky",
          top: 0,
          zIndex: 50,
          background: "#fff",
          borderBottom: "1px solid #e2e8f0",
          padding: "0 20px",
          height: 56,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        {/* LEFT */}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {/* MOBILE BURGER */}
          {isLoggedIn && (
            <button
              onClick={() => setMobileMenuOpen(true)}
              style={{
                display: "none",
                padding: 6,
                border: "1px solid #e2e8f0",
                borderRadius: 8,
                background: "#fff",
                cursor: "pointer",
              }}
              className="mobile-burger"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                style={{ width: 22, height: 22, color: "#374151" }}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          )}

          {/* DESKTOP DROPDOWNS */}
          <div style={{ display: "flex", alignItems: "center", gap: 6 }} className="desktop-nav">
            {isLoggedIn &&
              visibleSections.map((section) => (
                <NavDropdown
                  key={section.title}
                  section={section}
                  currentPath={location.pathname}
                  onNavigate={handleNavigate}
                />
              ))}
          </div>
        </div>

        {/* CENTER LOGO */}
        <div style={{ position: "absolute", left: "50%", transform: "translateX(-50%)" }}>
          <img
            src="/kyari_logo.webp"
            alt="Kyari"
            onClick={() => handleNavigate("/")}
            style={{ height: 36, objectFit: "contain", cursor: "pointer" }}
          />
        </div>

        {/* RIGHT */}
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {isLoggedIn && (
            <>
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#374151",
                  background: "#f1f5f9",
                  padding: "4px 10px",
                  borderRadius: 20,
                }}
                className="desktop-username"
              >
                {userName}
              </span>
              <button
                type="button"
                onClick={handleLogout}
                title="Logout"
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: 8,
                  background: "#fef2f2",
                  border: "1px solid #fecaca",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "background 0.15s",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "#fee2e2")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "#fef2f2")}
              >
                <img src="/power.png" alt="Logout" style={{ width: 16, height: 16 }} />
              </button>
            </>
          )}
        </div>
      </nav>

      {/* MOBILE SIDEBAR */}
      {mobileMenuOpen && (
        <>
          <div
            onClick={() => setMobileMenuOpen(false)}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.4)",
              zIndex: 40,
            }}
          />
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              height: "100%",
              width: 300,
              background: "#fff",
              zIndex: 50,
              boxShadow: "4px 0 24px rgba(15,23,42,0.15)",
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* SIDEBAR HEADER */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "14px 16px",
                borderBottom: "1px solid #e2e8f0",
              }}
            >
              <img src="/kyari_logo.webp" alt="Logo" style={{ height: 30, objectFit: "contain" }} />
              <button
                onClick={() => setMobileMenuOpen(false)}
                style={{ background: "none", border: "none", cursor: "pointer", padding: 4 }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ width: 22, height: 22, color: "#374151" }}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* USER BADGE */}
            <div
              style={{
                padding: "12px 16px",
                borderBottom: "1px solid #e2e8f0",
                background: "#f8fafc",
              }}
            >
              <p
                style={{
                  fontSize: 11,
                  color: "#94a3b8",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  marginBottom: 4,
                }}
              >
                Logged in as
              </p>
              <p style={{ fontSize: 14, fontWeight: 600, color: "#1e293b" }}>{userName} </p>
            </div>

            {/* LINKS */}
            <div style={{ flex: 1, padding: "12px 12px", overflowY: "auto" }}>
              {visibleSections.map((section) => {
                const visibleLinks = section.links.filter(canSeeLink);
                if (visibleLinks.length === 0) return null;
                return (
                  <div key={section.title} style={{ marginBottom: 20 }}>
                    <p
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: "#94a3b8",
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                        padding: "0 8px",
                        marginBottom: 6,
                      }}
                    >
                      {section.title}
                    </p>
                    {visibleLinks.map((link) => {
                      const isActive = location.pathname === link.path;
                      return (
                        <button
                          key={link.path}
                          onClick={() => handleNavigate(link.path)}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            width: "100%",
                            padding: "10px 12px",
                            borderRadius: 8,
                            border: "none",
                            background: isActive ? "#eef2ff" : "transparent",
                            color: isActive ? "#4f46e5" : "#374151",
                            fontWeight: isActive ? 600 : 400,
                            fontSize: 13,
                            cursor: "pointer",
                            textAlign: "left",
                            marginBottom: 2,
                            transition: "background 0.12s",
                          }}
                          onMouseEnter={(e) => {
                            if (!isActive) e.currentTarget.style.background = "#f1f5f9";
                          }}
                          onMouseLeave={(e) => {
                            if (!isActive) e.currentTarget.style.background = "transparent";
                          }}
                        >
                          {link.label}
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            style={{ width: 14, height: 14, opacity: 0.5 }}
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>

            {/* LOGOUT */}
            <div style={{ padding: "12px 16px", borderTop: "1px solid #e2e8f0" }}>
              <button
                onClick={handleLogout}
                style={{
                  width: "100%",
                  padding: "11px",
                  borderRadius: 10,
                  border: "none",
                  background: "#fef2f2",
                  color: "#dc2626",
                  fontWeight: 600,
                  fontSize: 14,
                  cursor: "pointer",
                  transition: "background 0.15s",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "#fee2e2")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "#fef2f2")}
              >
                Logout
              </button>
            </div>
          </div>
        </>
      )}

      <style>{`
        @media (max-width: 768px) {
          .desktop-nav { display: none !important; }
          .mobile-burger { display: flex !important; }
          .desktop-username { display: none !important; }
        }
      `}</style>
    </>
  );
}
